import React, { useState, useCallback, useRef } from 'react'
import {ArrowLeft, Upload, Plus, Trash2, Download, X, TrendingUp, BarChart3} from 'lucide-react'

interface CalibracionCalculatorProps {
  onBack: () => void
}

// ─── Tipos ────────────────────────────────────────────────────────────────────
type IndexMode = 'one' | 'two'

interface DataRow {
  id: number
  fecha: string
  iv1: string
  iv2: string
  variable: string
}

interface RegressionResult {
  name: string
  label: string
  color: string
  r2: number
  equation: string
  predict: (x: number) => number
  points: { x: number; y: number }[]
}

// ─── Regresiones ──────────────────────────────────────────────────────────────
function linspace(min: number, max: number, n = 100): number[] {
  return Array.from({ length: n }, (_, i) => min + (i / (n - 1)) * (max - min))
}

function mean(arr: number[]) {
  return arr.reduce((a, b) => a + b, 0) / arr.length
}

function r2score(yTrue: number[], yPred: number[]): number {
  const yMean = mean(yTrue)
  const ssTot = yTrue.reduce((s, y) => s + (y - yMean) ** 2, 0)
  const ssRes = yTrue.reduce((s, y, i) => s + (y - yPred[i]) ** 2, 0)
  if (ssTot === 0) return 1
  return 1 - ssRes / ssTot
}

function linearReg(xs: number[], ys: number[]): RegressionResult {
  const n = xs.length
  const sx = xs.reduce((a, b) => a + b, 0)
  const sy = ys.reduce((a, b) => a + b, 0)
  const sxy = xs.reduce((a, x, i) => a + x * ys[i], 0)
  const sx2 = xs.reduce((a, x) => a + x * x, 0)
  const b = (n * sxy - sx * sy) / (n * sx2 - sx * sx)
  const a = (sy - b * sx) / n
  const pred = xs.map(x => a + b * x)
  const sign = b >= 0 ? '+' : '-'
  const bAbs = Math.abs(b).toFixed(4)
  return {
    name: 'linear',
    label: 'Lineal',
    color: '#3b82f6',
    r2: r2score(ys, pred),
    equation: `y = ${a.toFixed(4)} ${sign} ${bAbs}x`,
    predict: (x: number) => a + b * x,
    points: [],
  }
}

function quadraticReg(xs: number[], ys: number[]): RegressionResult {
  const n = xs.length
  // solve normal equations for y = a + bx + cx²
  const s0 = n
  const s1 = xs.reduce((a, x) => a + x, 0)
  const s2 = xs.reduce((a, x) => a + x ** 2, 0)
  const s3 = xs.reduce((a, x) => a + x ** 3, 0)
  const s4 = xs.reduce((a, x) => a + x ** 4, 0)
  const sy = ys.reduce((a, y) => a + y, 0)
  const sxy = xs.reduce((a, x, i) => a + x * ys[i], 0)
  const sx2y = xs.reduce((a, x, i) => a + x ** 2 * ys[i], 0)
  // Gauss elimination 3x3
  const M = [
    [s0, s1, s2, sy],
    [s1, s2, s3, sxy],
    [s2, s3, s4, sx2y],
  ]
  for (let col = 0; col < 3; col++) {
    let maxR = col
    for (let r = col + 1; r < 3; r++) if (Math.abs(M[r][col]) > Math.abs(M[maxR][col])) maxR = r
    ;[M[col], M[maxR]] = [M[maxR], M[col]]
    for (let r = col + 1; r < 3; r++) {
      const f = M[r][col] / M[col][col]
      for (let c = col; c < 4; c++) M[r][c] -= f * M[col][c]
    }
  }
  const c = M[2][3] / M[2][2]
  const b = (M[1][3] - M[1][2] * c) / M[1][1]
  const a = (M[0][3] - M[0][2] * c - M[0][1] * b) / M[0][0]
  const pred = xs.map(x => a + b * x + c * x ** 2)
  const fmt = (v: number) => `${v >= 0 ? '+' : '-'}${Math.abs(v).toFixed(4)}`
  return {
    name: 'quadratic',
    label: 'Cuadrática',
    color: '#8b5cf6',
    r2: r2score(ys, pred),
    equation: `y = ${a.toFixed(4)} ${fmt(b)}x ${fmt(c)}x²`,
    predict: (x: number) => a + b * x + c * x ** 2,
    points: [],
  }
}

function powerReg(xs: number[], ys: number[]): RegressionResult | null {
  // y = a·xᵇ  → ln(y) = ln(a) + b·ln(x), requires x>0, y>0
  const pairs = xs.map((x, i) => ({ x, y: ys[i] })).filter(p => p.x > 0 && p.y > 0)
  if (pairs.length < 2) return null
  const lx = pairs.map(p => Math.log(p.x))
  const ly = pairs.map(p => Math.log(p.y))
  const res = linearReg(lx, ly)
  const b = res.predict(1) - res.predict(0) // slope
  const lnA = res.predict(0) - b * 0 // intercept = predict at lx=0
  const intercept_lna = ly.reduce((s, y, i) => s + y, 0) / ly.length - b * (lx.reduce((s, x) => s + x, 0) / lx.length)
  const a = Math.exp(intercept_lna)
  const pred = xs.map(x => (x > 0 ? a * x ** b : NaN))
  const validPred = pairs.map(p => a * p.x ** b)
  const validY = pairs.map(p => p.y)
  return {
    name: 'power',
    label: 'Potencial',
    color: '#f59e0b',
    r2: r2score(validY, validPred),
    equation: `y = ${a.toFixed(4)}·x^${b.toFixed(4)}`,
    predict: (x: number) => (x > 0 ? a * x ** b : NaN),
    points: [],
  }
}

function exponentialReg(xs: number[], ys: number[]): RegressionResult | null {
  // y = a·e^(bx) → ln(y) = ln(a) + bx, requires y>0
  const pairs = xs.map((x, i) => ({ x, y: ys[i] })).filter(p => p.y > 0)
  if (pairs.length < 2) return null
  const lx = pairs.map(p => p.x)
  const ly = pairs.map(p => Math.log(p.y))
  const res = linearReg(lx, ly)
  // Extract a and b properly
  const n = pairs.length
  const sx = lx.reduce((a, b) => a + b, 0)
  const sly = ly.reduce((a, b) => a + b, 0)
  const sxly = lx.reduce((a, x, i) => a + x * ly[i], 0)
  const sx2 = lx.reduce((a, x) => a + x * x, 0)
  const bVal = (n * sxly - sx * sly) / (n * sx2 - sx * sx)
  const lnA = (sly - bVal * sx) / n
  const a = Math.exp(lnA)
  const pred = pairs.map(p => a * Math.exp(bVal * p.x))
  const validY = pairs.map(p => p.y)
  return {
    name: 'exponential',
    label: 'Exponencial',
    color: '#ef4444',
    r2: r2score(validY, pred),
    equation: `y = ${a.toFixed(4)}·e^(${bVal.toFixed(4)}x)`,
    predict: (x: number) => a * Math.exp(bVal * x),
    points: [],
  }
}

function logarithmicReg(xs: number[], ys: number[]): RegressionResult | null {
  // y = a + b·ln(x), requires x>0
  const pairs = xs.map((x, i) => ({ x, y: ys[i] })).filter(p => p.x > 0)
  if (pairs.length < 2) return null
  const lx = pairs.map(p => Math.log(p.x))
  const ly = pairs.map(p => p.y)
  const n = pairs.length
  const slx = lx.reduce((a, b) => a + b, 0)
  const sly = ly.reduce((a, b) => a + b, 0)
  const slxly = lx.reduce((a, x, i) => a + x * ly[i], 0)
  const slx2 = lx.reduce((a, x) => a + x * x, 0)
  const b = (n * slxly - slx * sly) / (n * slx2 - slx * slx)
  const a = (sly - b * slx) / n
  const pred = pairs.map(p => a + b * Math.log(p.x))
  const sign = b >= 0 ? '+' : '-'
  return {
    name: 'logarithmic',
    label: 'Logarítmica',
    color: '#10b981',
    r2: r2score(ly, pred),
    equation: `y = ${a.toFixed(4)} ${sign} ${Math.abs(b).toFixed(4)}·ln(x)`,
    predict: (x: number) => (x > 0 ? a + b * Math.log(x) : NaN),
    points: [],
  }
}

// ─── Mini gráfica SVG ─────────────────────────────────────────────────────────
interface ChartProps {
  result: RegressionResult
  rawX: number[]
  rawY: number[]
  xLabel: string
  yLabel: string
}

const CHART_W = 480
const CHART_H = 320
const PAD = { top: 20, right: 20, bottom: 50, left: 60 }

function scaleX(v: number, min: number, max: number) {
  const w = CHART_W - PAD.left - PAD.right
  return PAD.left + ((v - min) / (max - min)) * w
}
function scaleY(v: number, min: number, max: number) {
  const h = CHART_H - PAD.top - PAD.bottom
  return PAD.top + h - ((v - min) / (max - min)) * h
}

const MiniChart: React.FC<ChartProps> = ({ result, rawX, rawY, xLabel, yLabel }) => {
  const xs = linspace(Math.min(...rawX), Math.max(...rawX), 120)
  const ys = xs.map(x => result.predict(x)).filter(y => isFinite(y) && !isNaN(y))
  const validXs = xs.filter((_, i) => isFinite(result.predict(xs[i])) && !isNaN(result.predict(xs[i])))

  const allY = [...rawY, ...ys].filter(isFinite)
  const allX = [...rawX, ...validXs].filter(isFinite)

  const xMin = Math.min(...allX)
  const xMax = Math.max(...allX)
  const yMin = Math.min(...allY)
  const yMax = Math.max(...allY)

  const xRange = xMax - xMin || 1
  const yRange = yMax - yMin || 1

  const xMinP = xMin - xRange * 0.05
  const xMaxP = xMax + xRange * 0.05
  const yMinP = yMin - yRange * 0.05
  const yMaxP = yMax + yRange * 0.05

  // Build curve path
  const curvePts = validXs.map(x => {
    const y = result.predict(x)
    return `${scaleX(x, xMinP, xMaxP).toFixed(1)},${scaleY(y, yMinP, yMaxP).toFixed(1)}`
  })
  const curvePath = curvePts.length > 1 ? `M ${curvePts.join(' L ')}` : ''

  // Y axis ticks
  const yTicks = Array.from({ length: 5 }, (_, i) => yMinP + (i / 4) * (yMaxP - yMinP))
  const xTicks = Array.from({ length: 5 }, (_, i) => xMinP + (i / 4) * (xMaxP - xMinP))

  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="w-full h-auto" style={{ background: '#fff' }}>
      {/* Grid lines */}
      {yTicks.map((t, i) => (
        <line
          key={i}
          x1={PAD.left} y1={scaleY(t, yMinP, yMaxP)}
          x2={CHART_W - PAD.right} y2={scaleY(t, yMinP, yMaxP)}
          stroke="#f1f5f9" strokeWidth="1"
        />
      ))}
      {xTicks.map((t, i) => (
        <line
          key={i}
          x1={scaleX(t, xMinP, xMaxP)} y1={PAD.top}
          x2={scaleX(t, xMinP, xMaxP)} y2={CHART_H - PAD.bottom}
          stroke="#f1f5f9" strokeWidth="1"
        />
      ))}

      {/* Axes */}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={CHART_H - PAD.bottom} stroke="#94a3b8" strokeWidth="1.5" />
      <line x1={PAD.left} y1={CHART_H - PAD.bottom} x2={CHART_W - PAD.right} y2={CHART_H - PAD.bottom} stroke="#94a3b8" strokeWidth="1.5" />

      {/* Y ticks + labels */}
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={PAD.left - 4} y1={scaleY(t, yMinP, yMaxP)} x2={PAD.left} y2={scaleY(t, yMinP, yMaxP)} stroke="#94a3b8" strokeWidth="1" />
          <text x={PAD.left - 8} y={scaleY(t, yMinP, yMaxP)} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="#64748b">
            {t.toFixed(2)}
          </text>
        </g>
      ))}
      {/* X ticks + labels */}
      {xTicks.map((t, i) => (
        <g key={i}>
          <line x1={scaleX(t, xMinP, xMaxP)} y1={CHART_H - PAD.bottom} x2={scaleX(t, xMinP, xMaxP)} y2={CHART_H - PAD.bottom + 4} stroke="#94a3b8" strokeWidth="1" />
          <text x={scaleX(t, xMinP, xMaxP)} y={CHART_H - PAD.bottom + 14} textAnchor="middle" fontSize="10" fill="#64748b">
            {t.toFixed(2)}
          </text>
        </g>
      ))}

      {/* Axis labels */}
      <text x={(PAD.left + CHART_W - PAD.right) / 2} y={CHART_H - 8} textAnchor="middle" fontSize="11" fill="#475569" fontWeight="600">{xLabel}</text>
      <text
        x={14} y={(PAD.top + CHART_H - PAD.bottom) / 2}
        textAnchor="middle" fontSize="11" fill="#475569" fontWeight="600"
        transform={`rotate(-90, 14, ${(PAD.top + CHART_H - PAD.bottom) / 2})`}
      >{yLabel}</text>

      {/* Regression curve */}
      {curvePath && (
        <path d={curvePath} fill="none" stroke={result.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      )}

      {/* Scatter points */}
      {rawX.map((x, i) => (
        <circle
          key={i}
          cx={scaleX(x, xMinP, xMaxP)}
          cy={scaleY(rawY[i], yMinP, yMaxP)}
          r="4"
          fill={result.color}
          fillOpacity="0.7"
          stroke="#fff"
          strokeWidth="1.5"
        />
      ))}
    </svg>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
const CalibracionCalculator: React.FC<CalibracionCalculatorProps> = ({ onBack }) => {
  const [indexMode, setIndexMode] = useState<IndexMode>('one')
  const [rows, setRows] = useState<DataRow[]>(() =>
    Array.from({ length: 4 }, (_, i) => ({ id: i + 1, fecha: '', iv1: '', iv2: '', variable: '' }))
  )
  const [nextId, setNextId] = useState(5)
  const [pasteText, setPasteText] = useState('')
  const [pasteError, setPasteError] = useState('')
  const [ivLabel, setIvLabel] = useState('NDVI')
  const [iv2Label, setIv2Label] = useState('EVI')
  const [varLabel, setVarLabel] = useState('Variable')
  const [results, setResults] = useState<{ iv: string; regressions: RegressionResult[] }[]>([])
  const [calcError, setCalcError] = useState('')
  const [selectedChart, setSelectedChart] = useState<{ ivIdx: number; regName: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // ── Helpers ──
  const updateRow = (id: number, field: keyof DataRow, value: string) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r))
  }

  const addRow = () => {
    setRows(prev => [...prev, { id: nextId, fecha: '', iv1: '', iv2: '', variable: '' }])
    setNextId(n => n + 1)
  }

  const removeRow = (id: number) => {
    setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev)
  }

  const clearAll = () => {
    setRows([{ id: 1, fecha: '', iv1: '', iv2: '', variable: '' }])
    setNextId(2)
    setPasteText('')
    setPasteError('')
    setResults([])
    setCalcError('')
    setSelectedChart(null)
  }

  // ── Paste import ──
  // Detecta si el primer valor de una fila es una fecha (no numérico)
  const looksLikeDate = (v: string) => v !== '' && isNaN(Number(v))

  const handlePaste = () => {
    setPasteError('')
    const lines = pasteText.split(/\n/).map(l => l.trim()).filter(l => l.length > 0)
    if (!lines.length) { setPasteError('No se encontraron datos.'); return }
    const newRows: DataRow[] = []
    let localId = nextId
    // La fecha es opcional: se necesitan al menos los índices + la variable
    const dataCols = indexMode === 'two' ? 3 : 2
    for (const line of lines) {
      const parts = line.split(/[,;\t]/).map(p => p.trim())
      // Si la primera columna parece una fecha, la usamos; si no, queda vacía
      const hasDate = parts.length > dataCols && looksLikeDate(parts[0])
      const fecha = hasDate ? parts[0] : ''
      const vals = hasDate ? parts.slice(1) : parts
      if (vals.length < dataCols) {
        setPasteError(`Formato incorrecto: "${line}" — se esperan al menos ${dataCols} columnas (${ivLabel}${indexMode === 'two' ? ',' + iv2Label : ''},${varLabel}). La fecha es opcional.`)
        return
      }
      if (indexMode === 'one') {
        newRows.push({ id: localId++, fecha, iv1: vals[0], iv2: '', variable: vals[1] })
      } else {
        newRows.push({ id: localId++, fecha, iv1: vals[0], iv2: vals[1], variable: vals[2] })
      }
    }
    setRows(prev => {
      const filtered = prev.filter(r => r.fecha !== '' || r.iv1 !== '' || r.variable !== '')
      return [...filtered, ...newRows]
    })
    setNextId(localId)
    setPasteText('')
  }

  // ── File import ──
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      setPasteText(text)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  // ── Calculate ──
  const calculate = () => {
    setCalcError('')
    setResults([])
    const valid = rows.filter(r => r.iv1 !== '' && r.variable !== '')
    if (valid.length < 3) { setCalcError('Se necesitan al menos 3 filas con datos completos.'); return }

    const xs1 = valid.map(r => parseFloat(r.iv1))
    const xs2 = indexMode === 'two' ? valid.map(r => parseFloat(r.iv2)) : []
    const ys = valid.map(r => parseFloat(r.variable))

    if (xs1.some(isNaN) || ys.some(isNaN) || (indexMode === 'two' && xs2.some(isNaN))) {
      setCalcError('Algunos valores no son numéricos. Verifica los datos.')
      return
    }

    const buildRegressions = (xs: number[], ys: number[]): RegressionResult[] => {
      const regs: (RegressionResult | null)[] = [
        linearReg(xs, ys),
        quadraticReg(xs, ys),
        powerReg(xs, ys),
        exponentialReg(xs, ys),
        logarithmicReg(xs, ys),
      ]
      return regs.filter((r): r is RegressionResult => r !== null)
    }

    const out: { iv: string; regressions: RegressionResult[] }[] = [
      { iv: ivLabel, regressions: buildRegressions(xs1, ys) },
    ]
    if (indexMode === 'two') {
      out.push({ iv: iv2Label, regressions: buildRegressions(xs2, ys) })
    }
    setResults(out)
    setSelectedChart(null)
  }

  // ── Download CSV ──
  const downloadCSV = () => {
    const header = indexMode === 'two'
      ? `Fecha,${ivLabel},${iv2Label},${varLabel}\n`
      : `Fecha,${ivLabel},${varLabel}\n`
    const body = rows.map(r =>
      indexMode === 'two'
        ? `${r.fecha},${r.iv1},${r.iv2},${r.variable}`
        : `${r.fecha},${r.iv1},${r.variable}`
    ).join('\n')
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'calibracion_HHTools.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Download results CSV ──
  const downloadResults = () => {
    let csv = ''
    results.forEach(({ iv, regressions }) => {
      csv += `Índice de Vegetación: ${iv}\n`
      csv += 'Modelo,Ecuación,R²\n'
      regressions.forEach(r => {
        csv += `${r.label},"${r.equation}",${r.r2.toFixed(6)}\n`
      })
      csv += '\n'
    })
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'resultados_calibracion_HHTools.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // current chart
  const validRows = rows.filter(r => r.iv1 !== '' && r.variable !== '')
  const xs1Num = validRows.map(r => parseFloat(r.iv1))
  const xs2Num = validRows.map(r => parseFloat(r.iv2))
  const ysNum = validRows.map(r => parseFloat(r.variable))

  const getChartData = (ivIdx: number) => ({
    xs: ivIdx === 0 ? xs1Num : xs2Num,
    ys: ysNum,
    xLabel: ivIdx === 0 ? ivLabel : iv2Label,
  })

  const R2_COLOR = (r2: number) =>
    r2 >= 0.9 ? 'text-green-700 bg-green-50' : r2 >= 0.7 ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button onClick={onBack} className="flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-3 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver a Herramientas</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Calibración de Índices de Vegetación</h1>
              <p className="text-sm text-slate-500">Correlación y regresión entre índice de vegetación y variable de campo — HHTools</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">

        {/* ── Configuración ── */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-500" />
            Configuración
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Modo índice */}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Número de índices</label>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setIndexMode('one')}
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${indexMode === 'one' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  1 Índice
                </button>
                <button
                  onClick={() => setIndexMode('two')}
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${indexMode === 'two' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  2 Índices
                </button>
              </div>
            </div>
            {/* Nombre IV1 */}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Nombre Índice 1 (X)</label>
              <input
                type="text"
                value={ivLabel}
                onChange={e => setIvLabel(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                placeholder="ej. NDVI"
              />
            </div>
            {/* Nombre IV2 */}
            {indexMode === 'two' && (
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Nombre Índice 2 (X₂)</label>
                <input
                  type="text"
                  value={iv2Label}
                  onChange={e => setIv2Label(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                  placeholder="ej. EVI"
                />
              </div>
            )}
            {/* Nombre variable */}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Nombre Variable Y</label>
              <input
                type="text"
                value={varLabel}
                onChange={e => setVarLabel(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                placeholder="ej. Rendimiento"
              />
            </div>
          </div>
        </section>

        {/* ── Tabla de datos ── */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold text-slate-800">Datos de entrada</h2>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={downloadCSV}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
              >
                <Download className="w-4 h-4" /> Exportar CSV
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <Upload className="w-4 h-4" /> Cargar archivo
              </button>
              <button
                onClick={clearAll}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-sm"
                title="Borra todos los datos y resultados"
              >
                <Trash2 className="w-4 h-4" /> Borrar todo
              </button>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
            </div>
          </div>

          {/* Zona de pegado */}
          <div className="mb-5 p-4 bg-slate-50 border border-dashed border-slate-300 rounded-xl">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Pegar datos</p>
            <p className="text-xs text-slate-500 mb-3">
              Formato: <code className="bg-slate-200 px-1 rounded font-mono">[Fecha,]{ivLabel}{indexMode === 'two' ? ',' + iv2Label : ''},{varLabel}</code> — una fila por línea. La <strong>fecha es opcional</strong>. Separa con coma, punto y coma o tabulador.
            </p>
            <div className="flex gap-2 items-start">
              <textarea
                value={pasteText}
                onChange={e => { setPasteText(e.target.value); setPasteError('') }}
                rows={3}
                placeholder={indexMode === 'two'
                  ? `0.65,0.55,3.2\n2024-01-08,0.70,0.60,3.8`
                  : `0.65,3.2\n2024-01-08,0.70,3.8`}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-violet-400 resize-none"
              />
              <button
                onClick={handlePaste}
                className="px-4 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 transition-colors shadow whitespace-nowrap"
              >
                Importar
              </button>
            </div>
            {pasteError && (
              <p className="mt-2 text-xs text-red-600 flex items-center gap-1"><X className="w-3 h-3" />{pasteError}</p>
            )}
          </div>

          {/* Tabla editable */}
          <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-lg border border-slate-100">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0">
                <tr className="bg-slate-100 text-slate-700">
                  <th className="px-3 py-2 text-left font-semibold w-8">#</th>
                  <th className="px-3 py-2 text-left font-semibold">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold">{ivLabel}</th>
                  {indexMode === 'two' && <th className="px-3 py-2 text-left font-semibold">{iv2Label}</th>}
                  <th className="px-3 py-2 text-left font-semibold">{varLabel}</th>
                  <th className="px-3 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-3 py-1 text-slate-400 font-mono text-xs">{idx + 1}</td>
                    <td className="px-2 py-1">
                      <input type="text" value={row.fecha} onChange={e => updateRow(row.id, 'fecha', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-violet-400" placeholder="YYYY-MM-DD" />
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" step="any" value={row.iv1} onChange={e => updateRow(row.id, 'iv1', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-violet-400" placeholder="0.00" />
                    </td>
                    {indexMode === 'two' && (
                      <td className="px-2 py-1">
                        <input type="number" step="any" value={row.iv2} onChange={e => updateRow(row.id, 'iv2', e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-violet-400" placeholder="0.00" />
                      </td>
                    )}
                    <td className="px-2 py-1">
                      <input type="number" step="any" value={row.variable} onChange={e => updateRow(row.id, 'variable', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-violet-400" placeholder="0.00" />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button onClick={() => removeRow(row.id)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3 mt-4">
            <button onClick={addRow} className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-violet-300 text-violet-500 hover:border-violet-400 hover:text-violet-600 rounded-lg text-sm font-medium transition-colors">
              <Plus className="w-4 h-4" /> Agregar fila
            </button>
          </div>
        </section>

        {/* ── Botón calcular ── */}
        {calcError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
            <X className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{calcError}</p>
          </div>
        )}
        <div className="flex justify-center">
          <button
            onClick={calculate}
            className="px-10 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-lg hover:from-violet-700 hover:to-indigo-700 transition-all hover:shadow-xl"
          >
            Calcular correlaciones
          </button>
        </div>

        {/* ── Resultados ── */}
        {results.length > 0 && (
          <section className="space-y-10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">Resultados de calibración</h2>
              <button onClick={downloadResults} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm">
                <Download className="w-4 h-4" /> Descargar resultados
              </button>
            </div>

            {results.map(({ iv, regressions }, ivIdx) => {
              const { xs, ys, xLabel } = getChartData(ivIdx)
              const sorted = [...regressions].sort((a, b) => b.r2 - a.r2)
              const best = sorted[0]

              return (
                <div key={iv} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  {/* Encabezado */}
                  <div className="px-6 py-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">Índice: <span className="text-violet-700">{iv}</span> vs <span className="text-indigo-700">{varLabel}</span></h3>
                      <p className="text-sm text-slate-500">{regressions.length} modelos evaluados • Mejor modelo: <strong className="text-violet-700">{best.label}</strong> (R² = {best.r2.toFixed(4)})</p>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Tabla resumen */}
                    <div className="overflow-x-auto rounded-xl border border-slate-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600">
                            <th className="px-4 py-3 text-left font-semibold">Modelo</th>
                            <th className="px-4 py-3 text-left font-semibold">Ecuación</th>
                            <th className="px-4 py-3 text-center font-semibold">R²</th>
                            <th className="px-4 py-3 text-center font-semibold">Ver gráfica</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((reg, ri) => (
                            <tr key={reg.name} className={`border-t border-slate-50 ${ri === 0 ? 'bg-violet-50/50' : ''}`}>
                              <td className="px-4 py-3 font-semibold flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full inline-block flex-shrink-0" style={{ background: reg.color }} />
                                {reg.label}
                                {ri === 0 && <span className="ml-1 text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-bold">Mejor</span>}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-slate-700">{reg.equation}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-block px-2 py-0.5 rounded font-bold text-xs ${R2_COLOR(reg.r2)}`}>
                                  {reg.r2.toFixed(4)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => setSelectedChart(
                                    selectedChart?.ivIdx === ivIdx && selectedChart?.regName === reg.name
                                      ? null
                                      : { ivIdx, regName: reg.name }
                                  )}
                                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${selectedChart?.ivIdx === ivIdx && selectedChart?.regName === reg.name ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-violet-100 hover:text-violet-700'}`}
                                >
                                  {selectedChart?.ivIdx === ivIdx && selectedChart?.regName === reg.name ? 'Ocultar' : 'Ver'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Gráficas */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {regressions.map(reg => (
                        <div
                          key={reg.name}
                          className={`rounded-xl border-2 overflow-hidden transition-all ${selectedChart?.ivIdx === ivIdx && selectedChart?.regName === reg.name ? 'border-violet-400 shadow-lg' : 'border-slate-100 hover:border-slate-200'}`}
                        >
                          <div className="px-4 py-2 flex items-center justify-between" style={{ background: reg.color + '18' }}>
                            <span className="text-xs font-bold" style={{ color: reg.color }}>{reg.label}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${R2_COLOR(reg.r2)}`}>R² = {reg.r2.toFixed(4)}</span>
                          </div>
                          <div className="p-2">
                            <MiniChart result={reg} rawX={xs.filter(isFinite)} rawY={ys.filter(isFinite)} xLabel={xLabel} yLabel={varLabel} />
                            <p className="text-center font-mono text-xs text-slate-500 mt-1 px-2 truncate">{reg.equation}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            })}
          </section>
        )}

        {/* Disclaimer */}
        <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <strong>Nota:</strong> Los modelos se ajustan usando mínimos cuadrados ordinarios. Para potencial, exponencial y logarítmica se requieren valores positivos en X y/o Y. El R² indica la proporción de varianza explicada por el modelo; valores ≥ 0.9 (verde) se consideran ajustes excelentes.
        </div>
      </main>
    </div>
  )
}

export default CalibracionCalculator
